import React, { useCallback, useEffect, useState } from "react";

import "./ApplicationList.css";
import { useAuth } from "../../../context/AuthContext";
import useApplicationList from "../../../hooks/useApplicationList";
import useApplicationActions from "../../../hooks/useApplicationActions";
import useModal from "../../../hooks/useModal";
import DataTableApp from "../../common/DataTableApp";
import ApplicationHeader from "../ApplicationHeader/ApplicationHeader";
import CopyApplicationModal from "../CopyApplicationModal/CopyApplicationModal";
import DeleteApplicationModal from "../DeleteApplicationModal/DeleteApplicationModal";
import LoadingSpinner from "../../common/LoadingSpinner";
import { useLocation } from "react-router-dom";

const ApplicationList = ({
  mode = "user",
  paymentMode = false,
  shippingMode = false,
}) => {
  const { chamberName } = useAuth();

  const location = useLocation();

  const { applications, loading, refresh } = useApplicationList({
    mode,
    paymentMode,
    shippingMode,
  });

  const { handleCopy, handleDelete, handleAssign } =
    useApplicationActions(refresh);

  const copyModal = useModal();
  const deleteModal = useModal();
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [selectedAppName, setSelectedAppName] = useState("");

  useEffect(() => {
    if (location.state?.__refresh) {
      refresh();
    }
  }, [location.state?.__refresh, refresh]);

  const handleOpenCopyModal = useCallback(
    (appId, appName) => {
      setSelectedAppId(appId);
      setSelectedAppName(appName);
      copyModal.open();
    },
    [copyModal],
  );

  const handleOpenDeleteModal = useCallback(
    (appId) => {
      setSelectedAppId(appId);
      deleteModal.open();
    },
    [deleteModal],
  );

  const handleConfirmCopy = useCallback(
    async (newName) => {
      await handleCopy(selectedAppId, newName);
      copyModal.close();
    },
    [selectedAppId, handleCopy, copyModal],
  );

  const handleConfirmDelete = useCallback(async () => {
    await handleDelete(selectedAppId);
    deleteModal.close();
  }, [selectedAppId, handleDelete, deleteModal]);

  const hiddenColumns = React.useMemo(() => {
    switch (mode) {
      case "operator":
        return ["Nazwa", "Obsługuje", "Data utworzenia", "Izba"];
      case "coordinator":
      case "shipping":
        return ["Nazwa", "Data utworzenia", "Izba"];
      case "user":
      default:
        return ["Obsługuje", "Izba"];
    }
  }, [mode]);

  const colorMode = mode === "user" ? "Client" : "Operator";

  return (
    <div className="card container-fluid shadow ms-4 px-4 py-3 w80">
      <div className="d-flex flex-column w72">
        <ApplicationHeader
          mode={mode}
          chamberName={chamberName}
          loading={loading}
          onAssign={() => handleAssign()}
        />

        {loading ? (
          <LoadingSpinner />
        ) : (
          <DataTableApp
            id={
              paymentMode ? "applications-toPay" : "application-list-standard"
            }
            data={applications}
            itemsPerPage={10}
            columnHeaders={[
              "Numer",
              "Nazwa",
              "Eksporter",
              "NIP",
              "Status",
              "Nr świadectwa",
              "Obsługuje",
              "Izba",
              "Data utworzenia",
              "Złożono",
              "Aktualizacja",
              "Action",
            ]}
            hiddenColumns={hiddenColumns}
            initialFilterStatuses={
              paymentMode
                ? ["do opłacenia", "do opłaty i podpisu", "opłata za usługę"]
                : shippingMode
                  ? ["do wydruku", "do wysyłki", "wysłano"]
                  : []
            }
            colorMode={colorMode}
            copyHandler={handleOpenCopyModal}
            deleteHandler={handleOpenDeleteModal}
            lockStatuses={paymentMode || shippingMode}
            showCaseMessageIndicator={
              mode === "operator" || mode === "coordinator"
            }
          />
        )}
      </div>
      {copyModal.isOpen && (
        <CopyApplicationModal
          appName={selectedAppName}
          onConfirm={handleConfirmCopy}
          onCancel={copyModal.close}
        />
      )}

      {deleteModal.isOpen && (
        <DeleteApplicationModal
          onConfirm={handleConfirmDelete}
          onCancel={deleteModal.close}
        />
      )}
    </div>
  );
};

export default ApplicationList;
